import { TYPE_OF_DAMAGE } from '@/api/types';
import { Options } from '@/components/ui/CheckboxGroup';
import { AdditionalInfo } from '@/features/v2/lib/additionalInfo';
import { DeadEnds, ExtendedSteps } from '@/features/v2/lib/wizard';
import {
  DAMAGE_RIGHTS_ASSIGNMENTS,
  DAMAGE_RIGHTS_ASSIGNMENTS_NAME,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupDamageRightsAssignments';
import {
  REPAIR_DOCUMENTS_PROVIDED_TO_IC,
  REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupRepairDocumentsProvided';
import { BEFORE_ACCIDENT_REPAIRED_NAME } from '@/features/v2/vehicle-damage/components/MultiSelectBeforeAccidentRepaired';
import { DAMAGE_BEFORE_ACCIDENT_NAME } from '@/features/v2/vehicle-damage/components/MultiSelectDamageBeforeAccident';
import { ORIGINAL_PARTS_USED_FOR_REPAIR_NAME } from '@/features/v2/vehicle-damage/components/OriginalPartsUsedForRepair';
import {
  ACCIDENT_IN_POLAND,
  ACCIDENT_IN_POLAND_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentInPoland';
import {
  ACCIDENT_REPORTED_TO_INSURER_NAME,
  ACCIDENT_REPORTED_TO_INSURER_VALUES,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentReportedToInsurer';
import {
  AGREEMENT_OR_SETTLEMENT,
  AGREEMENT_OR_SETTLEMENT_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupAgreementOrSettlement';
import {
  BUSINESS_ACTIVITY,
  BUSINESS_ACTIVITY_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupBusinessActivity';
import {
  HAS_DAMAGE_PHOTOS,
  HAS_DAMAGE_PHOTOS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupHasDamagePhotos';
import { INSURER_PAY_COMPENSATION_NAME } from '@/features/v2/vehicle-damage/components/RadioGroupInsurerPayCompensation';
import {
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT,
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupNoRepairableDamage';
import {
  POLISH_PLATES,
  POLISH_PLATES_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupPolishPlates';
import {
  REPAIRS_BEFORE_ACCIDENT,
  REPAIRS_BEFORE_ACCIDENT_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupPreAccidentRepairs';
import { REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME as REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_RADIO } from '@/features/v2/vehicle-damage/components/RadioGroupRepairDocumentsProvidedToInsurance';
import { TYPE_OF_DAMAGE_NAME } from '@/features/v2/vehicle-damage/components/RadioGroupTypeOfDamage';
import {
  VAT_PAYER_STATUS,
  VAT_PAYER_STATUS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVATTaxpayerStatus';
import { VEHICLE_AS_FIXED_ASSET_NAME } from '@/features/v2/vehicle-damage/components/RadioGroupVehicleAsFixedAsset';
import { STILL_VEHICLE_OWNER_NAME } from '@/features/v2/vehicle-damage/components/RadioGroupVehicleOwner';
import {
  VEHICLE_USED_FOR_BUSINESS,
  VEHICLE_USED_FOR_BUSINESS_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleUsedForBusiness';
import {
  VEHICLE_VALUE_BEFORE_CRASH,
  VEHICLE_VALUE_BEFORE_CRASH_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleValueBeforeCrash';
import {
  WHEEL_SIDE,
  WHEEL_SIDE_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSide';
import { WHEEL_SIDE_FACTORY_INSTALLED } from '@/features/v2/vehicle-damage/components/RadioGroupWheelSideForSure';
import { VAT_DEDUCTION_TYPE_NAME } from '@/features/v2/vehicle-damage/components/RadioVATDeductionPercentage';
import {
  AGREEMENT_OR_SETTLEMENT_CONCLUDED,
  AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME,
} from '@/features/v2/vehicle-damage/components/SelectAgreementOrSettlementConcluded';
import {
  DAMAGE_AOB_LIST_NAME,
  DAMAGE_RIGHTS_AUTHORIZATIONS_LIST_NAME,
  DAMAGE_RIGHTS_POA_LIST_NAME,
} from '@/features/v2/vehicle-damage/components/SelectAssignmentOfDamageRightsDocumentsSubject';
import { BUSINESS_TYPE_NAME } from '@/features/v2/vehicle-damage/components/SelectBusinessType';
import {
  CAR_CONDITION,
  CAR_CONDITION_NAME,
} from '@/features/v2/vehicle-damage/components/SelectCarCondition';
import { INSURANCE_COMPANY_NAME } from '@/features/v2/vehicle-damage/components/SelectInsuranceCompany';
import {
  applyDocumentStepRequirements,
  fullAC,
  fullACWithSale,
  fullOC,
  fullOCWithSale,
  partAC,
  partACWithSale,
  partOC,
  partOCWithSale,
} from '@/features/v2/vehicle-damage/const/documentsSteps';
import { Step1, Step2, Step3 } from '@/features/v2/vehicle-damage/steps';
import { SummarySchema } from '@/features/v2/vehicle-damage/summarySchema';
import { EnumLike } from '@/types';

import { i18n } from '@lingui/core';

export const VehicleDamageSchemaDeadEndsStepOne = {
  noAccidentReportedToInsurer: {
    condition: (val) =>
      val[ACCIDENT_REPORTED_TO_INSURER_NAME] ===
      ACCIDENT_REPORTED_TO_INSURER_VALUES.NO,
    returnToStep: 0,
  },
  noPolishInsurance: {
    condition: (val) =>
      val[POLISH_PLATES_NAME] === POLISH_PLATES.NO &&
      val[ACCIDENT_IN_POLAND_NAME] === ACCIDENT_IN_POLAND.NO,
    returnToStep: 1,
  },
  goingToBeSoldDamagedCarCondition: {
    condition: (val) =>
      val[CAR_CONDITION_NAME] === CAR_CONDITION.GOING_TO_BE_SOLD_DAMAGED,
    returnToStep: 1,
  },
  AgreementOrSettlementConcluded: {
    condition: (val) =>
      val[AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME]?.some(
        (item: EnumLike) =>
          item.value === AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS
      ),
    returnToStep: 2,
  },
  radioGroupVehicleValueBeforeCrash: {
    condition: (val) =>
      val[VEHICLE_VALUE_BEFORE_CRASH_NAME] ===
      VEHICLE_VALUE_BEFORE_CRASH.UNDER_10K,
    returnToStep: 1,
  },
  fullACGoingToBeSoldDamagedCarCondition: {
    condition: (val) =>
      val[CAR_CONDITION_NAME] === CAR_CONDITION.GOING_TO_BE_SOLD_DAMAGED,
    returnToStep: 1,
  },
  fullAgreementOrSettlementConcluded: {
    condition: (val) =>
      val[AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME]?.some(
        (item: EnumLike) =>
          item.value === AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS
      ),
    returnToStep: 2,
  },
} as const satisfies DeadEnds;

const documentsLastStep = (value: Record<string, unknown>) => {
  const hasSecondAdditionalStep =
    (value[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME] as Options[])?.some(
      (item: Options) => item.value !== REPAIR_DOCUMENTS_PROVIDED_TO_IC.ANY
    ) ?? false;

  const plusSecondAdditional = hasSecondAdditionalStep ? 1 : 0;

  if (
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_AC ||
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_OC
  ) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return {
        lastStep: 7 + plusSecondAdditional,
        additionalStep: true,
        secondAdditionalStep: hasSecondAdditionalStep,
      };
    }
    return {
      lastStep: 6 + plusSecondAdditional,
      additionalStep: false,
      secondAdditionalStep: hasSecondAdditionalStep,
    };
  }

  if (value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.PART_AC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return {
        lastStep: 5 + plusSecondAdditional,
        additionalStep: true,
        secondAdditionalStep: hasSecondAdditionalStep,
      };
    }
    return {
      lastStep: 4 + plusSecondAdditional,
      additionalStep: false,
      secondAdditionalStep: hasSecondAdditionalStep,
    };
  }

  if (value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.PART_OC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return {
        lastStep: 6 + plusSecondAdditional,
        additionalStep: true,
        secondAdditionalStep: hasSecondAdditionalStep,
      };
    }
    return {
      lastStep: 5 + plusSecondAdditional,
      additionalStep: false,
      secondAdditionalStep: hasSecondAdditionalStep,
    };
  }
};

const subSteps = (value: Record<string, unknown>) => {
  const withDamagePhotosRequired =
    value[HAS_DAMAGE_PHOTOS_NAME] === HAS_DAMAGE_PHOTOS.YES;
  const withInvoicesReceiptsOrOtherDocumentsRequired =
    (value[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME] as Options[])?.some(
      (item: Options) => item.value !== REPAIR_DOCUMENTS_PROVIDED_TO_IC.ANY
    ) ?? false;

  if (value.type_of_damage === TYPE_OF_DAMAGE.FULL_OC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return applyDocumentStepRequirements(
        withDamagePhotosRequired,
        withInvoicesReceiptsOrOtherDocumentsRequired,
        fullOCWithSale
      );
    }
    return applyDocumentStepRequirements(
      withDamagePhotosRequired,
      withInvoicesReceiptsOrOtherDocumentsRequired,
      fullOC
    );
  }
  if (value.type_of_damage === TYPE_OF_DAMAGE.PART_OC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return applyDocumentStepRequirements(
        withDamagePhotosRequired,
        withInvoicesReceiptsOrOtherDocumentsRequired,
        partOCWithSale
      );
    }
    return applyDocumentStepRequirements(
      withDamagePhotosRequired,
      withInvoicesReceiptsOrOtherDocumentsRequired,
      partOC
    );
  }
  if (value.type_of_damage === TYPE_OF_DAMAGE.PART_AC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return applyDocumentStepRequirements(
        withDamagePhotosRequired,
        withInvoicesReceiptsOrOtherDocumentsRequired,
        partACWithSale
      );
    }
    return applyDocumentStepRequirements(
      withDamagePhotosRequired,
      withInvoicesReceiptsOrOtherDocumentsRequired,
      partAC
    );
  }
  if (value.type_of_damage === TYPE_OF_DAMAGE.FULL_AC) {
    if (
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED ||
      value[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED
    ) {
      return applyDocumentStepRequirements(
        withDamagePhotosRequired,
        withInvoicesReceiptsOrOtherDocumentsRequired,
        fullACWithSale
      );
    }
    return applyDocumentStepRequirements(
      withDamagePhotosRequired,
      withInvoicesReceiptsOrOtherDocumentsRequired,
      fullAC
    );
  }
};

const stepOneProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>,
  additionalInfo: AdditionalInfo
) => {
  const points = [
    ACCIDENT_REPORTED_TO_INSURER_NAME,
    INSURANCE_COMPANY_NAME,
    TYPE_OF_DAMAGE_NAME,
    ACCIDENT_IN_POLAND_NAME,
    CAR_CONDITION_NAME,
    WHEEL_SIDE_NAME,
    INSURER_PAY_COMPENSATION_NAME,
    AGREEMENT_OR_SETTLEMENT_NAME,
    REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_RADIO,
    REPAIRS_BEFORE_ACCIDENT_NAME,
  ];

  if (
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.PART_OC ||
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_OC
  ) {
    points.push(POLISH_PLATES_NAME);
  }

  if (
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_AC ||
    value[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_OC
  ) {
    points.push(VEHICLE_VALUE_BEFORE_CRASH_NAME);
  }

  if (value[WHEEL_SIDE_NAME] === WHEEL_SIDE.LEFT) {
    points.push(WHEEL_SIDE_FACTORY_INSTALLED);
  }

  if (value[AGREEMENT_OR_SETTLEMENT_NAME] === AGREEMENT_OR_SETTLEMENT.YES) {
    points.push(AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME);
  }

  if (additionalInfo.isVehicleSold) {
    points.push(UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME);
  }
  if (
    value[UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME] ===
    UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT.YES
  ) {
    points.push(DAMAGE_BEFORE_ACCIDENT_NAME);
  }

  if (value[REPAIRS_BEFORE_ACCIDENT_NAME] === REPAIRS_BEFORE_ACCIDENT.YES) {
    points.push(ORIGINAL_PARTS_USED_FOR_REPAIR_NAME);
    points.push(BEFORE_ACCIDENT_REPAIRED_NAME);
  }

  if (!additionalInfo.isVehicleSold) {
    points.push(STILL_VEHICLE_OWNER_NAME);
  }

  return points;
};

const stepThreeProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>
) => {
  const points = [
    // Survey Three
    BUSINESS_ACTIVITY_NAME,
    // Survey Four
    DAMAGE_RIGHTS_ASSIGNMENTS_NAME,
  ];

  if (value[BUSINESS_ACTIVITY_NAME] === BUSINESS_ACTIVITY.YES) {
    points.push(VEHICLE_USED_FOR_BUSINESS_NAME);

    if (
      value[VEHICLE_USED_FOR_BUSINESS_NAME] === VEHICLE_USED_FOR_BUSINESS.YES
    ) {
      points.push(BUSINESS_TYPE_NAME);
    }

    if (value[BUSINESS_TYPE_NAME]) {
      points.push(VEHICLE_AS_FIXED_ASSET_NAME);
    }

    if (value[VEHICLE_AS_FIXED_ASSET_NAME]) {
      points.push(VAT_PAYER_STATUS_NAME);
    }

    if (value[VAT_PAYER_STATUS_NAME] === VAT_PAYER_STATUS.YES) {
      points.push(VAT_DEDUCTION_TYPE_NAME);
    }
  }

  function checkDamageRightsAssignmentsValue(target: string) {
    return value[DAMAGE_RIGHTS_ASSIGNMENTS_NAME]?.map(
      (item: Options) => item.value
    )?.includes(target);
  }

  if (
    !checkDamageRightsAssignmentsValue(DAMAGE_RIGHTS_ASSIGNMENTS.ANY) &&
    checkDamageRightsAssignmentsValue(DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_AOB)
  ) {
    points.push(DAMAGE_AOB_LIST_NAME);
  }

  if (
    !checkDamageRightsAssignmentsValue(DAMAGE_RIGHTS_ASSIGNMENTS.ANY) &&
    checkDamageRightsAssignmentsValue(
      DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_RIGHTS_AUTHORIZATIONS
    )
  ) {
    points.push(DAMAGE_RIGHTS_AUTHORIZATIONS_LIST_NAME);
  }

  if (
    !checkDamageRightsAssignmentsValue(DAMAGE_RIGHTS_ASSIGNMENTS.ANY) &&
    checkDamageRightsAssignmentsValue(
      DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_RIGHTS_POA
    )
  ) {
    points.push(DAMAGE_RIGHTS_POA_LIST_NAME);
  }

  return points;
};

export const VehicleDamageSchema = [
  {
    label: i18n.t('Weryfikacja szkody'),
    component: Step1,
    lastStep: () => {
      return {
        lastStep: 4,
        additionalStep: false,
      };
    },
    deadEnds: VehicleDamageSchemaDeadEndsStepOne,
    progressPoints: stepOneProgressPoints,
    chatMessage: (userName) => {
      return (
        <>
          {i18n.t('Cześć,')} {userName}!{' '}
          {i18n.t(
            'Pomogę Ci przejść przez etap weryfikacji sprawy. Udziel odpowiedzi na kilka podstawowych pytań i przyspiesz wyliczenie kwoty dopłaty do odszkodowania.'
          )}
        </>
      );
    },
    visibleInProgressBar: (step) => {
      return step < 4;
    },
    completeValidation: (innerStep) => {
      return innerStep && innerStep === 4;
    },
    backButton: (innerStep: number) => {
      return innerStep && innerStep === 4;
    },
  },
  {
    label: i18n.t('Dokumenty'),
    component: Step2,
    backButton: (innerStep: number) => {
      return innerStep && innerStep > 0;
    },
    subSteps: subSteps,
    lastStep: documentsLastStep,
    chatMessage: (_) => {
      return i18n.t(
        'Czas na dokumenty potrzebne do analizy Twojej sprawy. Do wyliczenia kwoty dopłaty niezbędny jest kosztorys naprawy pojazdu. Otrzymałeś go od ubezpieczyciela na adres e-mail. Załącz go teraz i przyspiesz analizę Twojej sprawy o 30%!'
      );
    },
    visibleInProgressBar: (step) => {
      return step < 4;
    },
  },
  {
    label: i18n.t('Dodatkowe informacje'),
    lastStep: () => {
      return {
        lastStep: 0,
        additionalStep: false,
      };
    },
    backButton: (innerStep, value) => {
      if (!value) return false;

      const repairDocumentsEmpty =
        !value[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME] ||
        value[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME].length === 0;

      const damageRightsEmpty =
        !value[DAMAGE_RIGHTS_ASSIGNMENTS_NAME] ||
        value[DAMAGE_RIGHTS_ASSIGNMENTS_NAME].length === 0;

      if (innerStep === 2 && repairDocumentsEmpty && damageRightsEmpty) {
        return true;
      }
      return false;
    },
    component: Step3,
    progressPoints: stepThreeProgressPoints,
    completeValidation: () => {
      return true;
    },
    chatMessage: (userName, innerStep) => {
      if (innerStep === 1) {
        return i18n.t(
          `Jeszcze chwila, jeszcze trochę... Już 80% pytań za Tobą. Poświęć jeszcze jedną minutę na odpowiedzi związane z historią sprawy i przejdź do zakończenia zgłoszenia.`
        );
      }
      return (
        <>
          {userName}, {i18n.t('dzięki Twojej współpracy')}{' '}
          {userName.length < 17 ? i18n.t('z rzeczoznawcą') : ''}{' '}
          {i18n.t(
            'możemy efektywnie przejść przez proces analizy i dojść do rozwiązania Twojego problemu w jak najszybszy sposób.'
          )}
        </>
      );
    },
    visibleInProgressBar: (step) => {
      return step < 4;
    },
  },
  {
    label: i18n.t('Zakończ'),
    visibleInProgressBar: (step) => {
      return step < 4;
    },
  },
  ...SummarySchema,
] as ExtendedSteps[];
